import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './Game.css';
import { db, auth, anonSignIn, googleProvider, signInWithPopup, signOut, onAuthStateChanged, initializeAnalytics, logEvent } from './firebase';
import Confetti from 'react-confetti';
import i18n from './i18n';
import { ref, get, set } from "firebase/database"; 
import { useTranslation } from 'react-i18next';

// Import Components
import Header from './components/Header';
import Board from './components/Board';
import InputBar from './components/InputBar';
import Keyboard from './components/Keyboard';
import CoinReward from './components/CoinReward';
import './components/CoinReward.css';
import LevelTransition from './components/LevelTransition';
import CookieConsentBanner from './components/CookieConsentBanner';

import HomePage from './components/HomePage'; 
import LevelList from './components/LevelList'; 
import Menu from './components/Menu'; 
import About from './components/pages/About';
import Contact from './components/pages/Contact';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import HowToPlay from './components/pages/HowToPlay';
import FAQ from './components/pages/FAQ';
import Terms from './components/pages/Terms';
import Categories from './components/pages/Categories';
import Guides from './components/pages/Guides';
import GuideArticle from './components/pages/GuideArticle';
import Updates from './components/pages/Updates';

const backgroundGradients = [
  'linear-gradient(to bottom, #ff7e5f, #feb47b)',
  'linear-gradient(to bottom, #00c9ff, #92fe9d)', 
  'linear-gradient(to bottom, #fc5c7d, #6a82fb)', // Nível 3
  'linear-gradient(to bottom, #f7971e, #ffd200)',
  'linear-gradient(to bottom, #473B7B, #35889A)',
  'linear-gradient(to bottom, #FF5858, #F09819)',
  'linear-gradient(to bottom, #00FF87, #60BF80)',
  'linear-gradient(to bottom, #5433FF, #A5FECB)',
  'linear-gradient(to bottom, #F77062, #FE5196)',
  'linear-gradient(to bottom, #4BC0C8, #C779D0)',
];

const HOME_BACKGROUND = 'linear-gradient(to bottom, #fc5c7d, #6a82fb)';

// --- MUDANÇA: Fundo Azul Royal para páginas de texto ---
const PAGES_BACKGROUND = 'linear-gradient(to bottom, #1e3c72, #2a5298)';

const SUPPORTED_LANGS = ['en', 'pt', 'es'];
const SITE_URL = 'https://top5game.com';

const PATH_TO_VIEW = {
  '/': 'home',
  '/levels': 'levels',
  '/game': 'game',
  '/about': 'about',
  '/contact': 'contact',
  '/privacy': 'privacy',
  '/how-to-play': 'how-to-play',
  '/faq': 'faq',
  '/terms': 'terms',
  '/categories': 'categories',
  '/guides': 'guides',
  '/updates': 'updates',
};

const VIEW_TO_PATH = Object.fromEntries(Object.entries(PATH_TO_VIEW).map(([path, view]) => [view, path]));

const stripTrailingSlash = (pathname) => {
  if (pathname === '/') return pathname;
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
};

const parseLangFromPathname = (pathname) => {
  const normalized = stripTrailingSlash(pathname);
  const parts = normalized.split('/').filter(Boolean);
  const first = parts[0];
  if (SUPPORTED_LANGS.includes(first)) {
    const restParts = parts.slice(1);
    const rest = restParts.length ? `/${restParts.join('/')}` : '/';
    return { lang: first, restPath: rest };
  }
  return { lang: null, restPath: normalized || '/' };
};

const getRouteFromPathname = (pathname) => {
  const { lang, restPath } = parseLangFromPathname(pathname);
  if (PATH_TO_VIEW[restPath]) return { view: PATH_TO_VIEW[restPath], guideSlug: null, lang };

  if (restPath.startsWith('/guides/')) {
    const slug = restPath.split('/')[2] || null;
    return { view: 'guide', guideSlug: slug, lang };
  }

  return { view: PATH_TO_VIEW[stripTrailingSlash(restPath)] || 'home', guideSlug: null, lang };
};

const ensureLinkTag = ({ rel, id, href, hreflang }) => {
  const existing = document.head.querySelector(`link#${id}`);
  const link = existing || document.createElement('link');
  link.rel = rel;
  link.id = id;
  link.href = href;
  if (hreflang) link.setAttribute('hreflang', hreflang);
  if (!existing) document.head.appendChild(link);
};

const ensureMetaTag = ({ name, property, id, content }) => {
  const selector = id ? `meta#${id}` : name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  const existing = document.head.querySelector(selector);
  const meta = existing || document.createElement('meta');
  if (id) meta.id = id;
  if (name) meta.setAttribute('name', name);
  if (property) meta.setAttribute('property', property);
  meta.setAttribute('content', content);
  if (!existing) document.head.appendChild(meta);
};

const setTitleAndDescription = ({ title, description, canonicalUrl }) => {
  document.title = title;
  ensureMetaTag({ name: 'description', id: 'meta-description', content: description });
  ensureMetaTag({ property: 'og:title', id: 'og-title', content: title });
  ensureMetaTag({ property: 'og:description', id: 'og-description', content: description });
  ensureMetaTag({ property: 'og:url', id: 'og-url', content: canonicalUrl });
  ensureMetaTag({ property: 'twitter:card', id: 'tw-card', content: 'summary' });
  ensureMetaTag({ property: 'twitter:title', id: 'tw-title', content: title });
  ensureMetaTag({ property: 'twitter:description', id: 'tw-desc', content: description });
};

const updateHeadForRoute = ({ lang, view, guideSlug }) => {
  document.documentElement.lang = lang;

  const buildPath = (targetLang) => {
    const base = `/${targetLang}`;
    if (view === 'home') return base;
    if (view === 'guide' && guideSlug) return `${base}/guides/${guideSlug}`;
    const sub = VIEW_TO_PATH[view] || '/';
    return `${base}${sub}`;
  };

  const canonicalUrl = `${SITE_URL}${buildPath(lang)}`;
  ensureLinkTag({ rel: 'canonical', id: 'canonical-link', href: canonicalUrl });

  // Title + description per route/language (translation-backed)
  const baseTitle = i18n.t('meta_site_title');
  const pageTitleKey = view === 'guide' && guideSlug ? `meta_guide_${guideSlug}_title` : `meta_${view}_title`;
  const pageDescKey = view === 'guide' && guideSlug ? `meta_guide_${guideSlug}_desc` : `meta_${view}_desc`;
  const pageTitle = i18n.exists(pageTitleKey) ? i18n.t(pageTitleKey) : i18n.t('meta_default_title');
  const pageDesc = i18n.exists(pageDescKey) ? i18n.t(pageDescKey) : i18n.t('meta_default_desc');
  const fullTitle = pageTitle === baseTitle ? baseTitle : `${pageTitle} | ${baseTitle}`;
  setTitleAndDescription({ title: fullTitle, description: pageDesc, canonicalUrl });

  for (const l of SUPPORTED_LANGS) {
    ensureLinkTag({
      rel: 'alternate',
      id: `alt-${l}`,
      href: `${SITE_URL}${buildPath(l)}`,
      hreflang: l,
    });
  }
  ensureLinkTag({
    rel: 'alternate',
    id: 'alt-x-default',
    href: `${SITE_URL}${buildPath('en')}`,
    hreflang: 'x-default',
  });
};

const loadGameState = (userId) => {
    if (!userId) return { level: 1, coins: 200, guessedWords: [] };
    try {
      const savedState = localStorage.getItem(`top5gameState_${userId}`);
      if (savedState === null) {
        return { level: 1, coins: 200, guessedWords: [] };
      }
      const parsed = JSON.parse(savedState);
      return {
        level: parsed.level || 1,
        coins: parsed.coins !== undefined ? parsed.coins : 200,
        guessedWords: parsed.guessedWords || [],
      };
    } catch (error) {
      console.error("Failed to load game state:", error);
      return { level: 1, coins: 200, guessedWords: [] };
    }
};

const getRevealedStorageKey = (userId, levelNum, lang) =>
  `top5gameRevealed_${userId}_${lang || 'en'}_${Number(levelNum) || 1}`;

const getInitialLang = () => {
  const browserLang = navigator.language.split('-')[0];
  const supportedLangs = ['en', 'es', 'pt'];
  if (supportedLangs.includes(browserLang)) {
    return browserLang;
  }
  return 'en';
};

const normalizeString = (str) => {
  // Normaliza para comparar respostas ignorando acentos, espaços e pontuação.
  // Ex.: "ARCO-ÍRIS" ~= "arco iris" ~= "arcoiris"
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
};

const loadAdSenseScript = () => {
  const scriptSrc = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-5052222576952627`;
  if (document.querySelector(`script[src*="${scriptSrc}"]`)) {
    return;
  }
  const script = document.createElement('script');
  script.async = true;
  script.src = scriptSrc;
  script.setAttribute('crossorigin', 'anonymous');
  document.head.appendChild(script);
};

function Game() {
  // Ensures translations are loaded (Suspense) for prerendering and avoids flashing keys.
  useTranslation();

  const initialRoute = getRouteFromPathname(window.location.pathname);

  const [levels, setLevels] = useState({});
  const [totalLevelsInDb, setTotalLevelsInDb] = useState(0);
  
  const [userId, setUserId] = useState(null);
  const [userIsAnon, setUserIsAnon] = useState(true);
  const [userName, setUserName] = useState(null);
  const [analyticsReady, setAnalyticsReady] = useState(false);

  const [currentLevelNum, setCurrentLevelNum] = useState(1);
  const [coins, setCoins] = useState(200);
  const [guessedWords, setGuessedWords] = useState([]);
  
  const gameStateRef = useRef({ level: 1, coins: 200, guessedWords: [] });

  const [isLoading, setIsLoading] = useState(true);
  const [viewState, setViewState] = useState(() => initialRoute.view);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [currentInput, setCurrentInput] = useState("");
  const [revealedLetters, setRevealedLetters] = useState(new Set());
  const [guessStatus, setGuessStatus] = useState('idle');
  const [lastGuessedWord, setLastGuessedWord] = useState(null);
  const [showCoinReward, setShowCoinReward] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [transitionState, setTransitionState] = useState('idle');
  const [userLang, setUserLang] = useState(() => initialRoute.lang || getInitialLang());
  const [guideSlug, setGuideSlug] = useState(() => initialRoute.guideSlug || null);
  const [justRevealedLetter, setJustRevealedLetter] = useState(null);

  useEffect(() => {
    i18n.changeLanguage(userLang);
  }, [userLang]);

  useEffect(() => {
    updateHeadForRoute({ lang: userLang, view: viewState, guideSlug });
  }, [userLang, viewState, guideSlug]);

  useEffect(() => {
    if (!initialRoute.lang) {
      const base = `/${userLang}`;
      let nextPath = base;
      if (viewState === 'guide' && guideSlug) nextPath = `${base}/guides/${guideSlug}`;
      else if (viewState !== 'home') nextPath = `${base}${VIEW_TO_PATH[viewState] || '/'}`;
      window.history.replaceState({ view: viewState, guideSlug }, '', nextPath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gameStateRef.current = { level: currentLevelNum, coins, guessedWords };
  }, [currentLevelNum, coins, guessedWords]);

  const resetTransientStates = useCallback(() => {
    setCurrentInput("");
    setLastGuessedWord(null);
  }, []);

  const handleCookieAccept = useCallback(() => {
    initializeAnalytics();
    loadAdSenseScript();
    setAnalyticsReady(true);
  }, []);

  const handleCookieReject = useCallback(() => {
    setAnalyticsReady(false);
  }, []);

  // --- Auth & State Management ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        setUserIsAnon(user.isAnonymous);
        setUserName(user.isAnonymous ? null : user.displayName);

        if (!user.isAnonymous) {
            const userRef = ref(db, `users/${user.uid}`);
            const snapshot = await get(userRef);
            const savedState = loadGameState(user.uid);

            let newLevel = savedState.level;
            let newCoins = savedState.coins;

            if (snapshot.exists()) {
                const data = snapshot.val();
                if (data.level > newLevel) newLevel = data.level;
                if (data.coins) newCoins = data.coins;
            } else {
                await set(userRef, {
                    email: user.email,
                    level: newLevel,
                    coins: newCoins,
                });
            }

            setCurrentLevelNum(newLevel);
            setCoins(newCoins);
            setGuessedWords(savedState.guessedWords);
            resetTransientStates();

            if (analyticsReady) {
              logEvent(initializeAnalytics(), 'user_logged_in', {
                  user_id: user.uid,
                  auth_method: 'google'
              });
            }
        } else {
            const savedState = loadGameState(user.uid);
            setCurrentLevelNum(savedState.level);
            setCoins(savedState.coins);
            setGuessedWords(savedState.guessedWords);
            resetTransientStates();
        }
      } else {
        const anonUser = await anonSignIn();
        if (anonUser) {
             setUserId(anonUser.uid);
             setUserIsAnon(true);
             const savedState = loadGameState(anonUser.uid);
             setCurrentLevelNum(savedState.level);
             setCoins(savedState.coins);
             setGuessedWords(savedState.guessedWords);
             resetTransientStates();
        } else {
             setUserId(null);
             setUserIsAnon(true);
        }
      }
      
      if (isLoading) {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [isLoading, analyticsReady, resetTransientStates]);

  useEffect(() => {
    if (!userId) return;
    const gameState = {
      level: currentLevelNum,
      coins: coins,
      guessedWords: guessedWords,
    };
    localStorage.setItem(`top5gameState_${userId}`, JSON.stringify(gameState));
  }, [currentLevelNum, coins, guessedWords, userId, userIsAnon]); 

  const revealedStorageKeyRef = useRef(null);
  const currentLevelNumRef = useRef(currentLevelNum);
  const userLangRef = useRef(userLang);

  useEffect(() => {
    currentLevelNumRef.current = currentLevelNum;
    userLangRef.current = userLang;
  }, [currentLevelNum, userLang]);

  useEffect(() => {
    if (!userId) return;
    const key = getRevealedStorageKey(userId, currentLevelNum, userLang);
    revealedStorageKeyRef.current = key;
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : [];
      setRevealedLetters(new Set(Array.isArray(parsed) ? parsed : []));
    } catch (e) {
      console.error('Failed to load revealed letters:', e);
      setRevealedLetters(new Set());
    }
  }, [currentLevelNum, userLang, userId]);

  useEffect(() => {
    if (!userId) return;
    const key =
      revealedStorageKeyRef.current ||
      getRevealedStorageKey(userId, currentLevelNumRef.current, userLangRef.current);
    try {
      localStorage.setItem(key, JSON.stringify(Array.from(revealedLetters)));
    } catch (e) {
      console.error('Failed to save revealed letters:', e);
    }
  }, [revealedLetters, userId]);

  // --- Game Logic ---

  const handleLanguageChange = (lang) => {
    setUserLang(lang);
    i18n.changeLanguage(lang);
    const base = `/${lang}`;
    let nextPath = base;
    if (viewState === 'guide' && guideSlug) nextPath = `${base}/guides/${guideSlug}`;
    else if (viewState !== 'home') nextPath = `${base}${VIEW_TO_PATH[viewState] || '/'}`;
    window.history.pushState({ view: viewState, guideSlug }, '', nextPath);
  };
  
  // --- CARREGAMENTO DE DADOS DOS NÍVEIS ---
  const loadLevelData = useCallback(async () => {
      try {
        const totalRef = ref(db, 'levels_total');
        const totalSnapshot = await get(totalRef);
        const totalInDb = totalSnapshot.val() || 0;
        setTotalLevelsInDb(totalInDb);

        const cachedLevels = localStorage.getItem('game_levels');
        let parsedCachedLevels = cachedLevels ? JSON.parse(cachedLevels) : {};

        // Carrega TODOS os níveis
        const levelsRef = ref(db, 'levels');
        const snapshot = await get(levelsRef);

        if (snapshot.exists()) {
            const allLevelsData = snapshot.val();
            setLevels(allLevelsData);
            localStorage.setItem('game_levels', JSON.stringify(allLevelsData));
        } else {
            if (Object.keys(parsedCachedLevels).length > 0) {
                setLevels(parsedCachedLevels);
            }
        }
      } catch (error) {
        console.error("Failed to load levels data:", error);
      }
    }, []); 

	  useEffect(() => {
	    const needsLevelData = viewState === 'game' || viewState === 'levels' || viewState === 'categories';
	    if (!isLoading && needsLevelData && navigator.userAgent !== 'ReactSnap') {
	      loadLevelData();
	    }
	  }, [isLoading, loadLevelData, viewState]);


  const currentLevelData = levels[`level_${currentLevelNum}`];
  const correctAnswers = useMemo(() => {
    if (!currentLevelData) return [];
    if (currentLevelData.answersByLang && Array.isArray(currentLevelData.answersByLang[userLang])) {
      return currentLevelData.answersByLang[userLang].map((a) => String(a).toUpperCase());
    }
    return [];
  }, [currentLevelData, userLang]);
  const currentTheme = useMemo(() => {
    return currentLevelData?.themes[userLang] ?? "";
  }, [currentLevelData, userLang]);
  const unguessedWords = correctAnswers.filter(a => !guessedWords.includes(a));
  
  const validChars = useMemo(() => {
    const allAnswersString = unguessedWords.join('');
    const normalizedString = normalizeString(allAnswersString);
    const letters = new Set(normalizedString.replace(/[^A-Z]/g, '').split(''));
    // Espaços/hífens/pontuação não precisam ser digitados; são ignorados na comparação.
    return { letters, special: new Set() };
  }, [unguessedWords]);
  
  // --- MUDANÇA: Lógica do Background ---
  let displayedBackground;
  if (viewState === 'home' || viewState === 'levels') {
      displayedBackground = HOME_BACKGROUND;
  } else if (viewState === 'about' || viewState === 'contact' || viewState === 'privacy' || viewState === 'how-to-play' || viewState === 'faq' || viewState === 'terms' || viewState === 'categories' || viewState === 'guides' || viewState === 'guide') {
      // Usa o novo azul royal para páginas de texto
      displayedBackground = PAGES_BACKGROUND;
  } else {
      displayedBackground = backgroundGradients[(currentLevelNum - 1) % backgroundGradients.length];
  }

  useEffect(() => {
    const onPopState = () => {
      const route = getRouteFromPathname(window.location.pathname);
      if (route.lang && route.lang !== userLang) {
        setUserLang(route.lang);
        i18n.changeLanguage(route.lang);
      }
      setGuideSlug(route.guideSlug || null);
      setViewState(route.view);
      setIsMenuOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [userLang]);

  const advanceToNextLevel = useCallback(async () => {
    setShowConfetti(false);
    const nextLevelNum = currentLevelNum + 1;
    
    if (!userIsAnon && userId) {
        const userRef = ref(db, `users/${userId}`);
        const currentData = gameStateRef.current;
        await set(userRef, {
            email: auth.currentUser?.email,
            level: nextLevelNum,
            coins: currentData.coins,
        }).catch((err) => console.error("Erro ao salvar progresso:", err));
    }
    
    if (nextLevelNum > totalLevelsInDb) {
      alert("You've finished all levels!");
      setTransitionState('idle');
      return;
    }
    setGuessedWords([]);
    setCurrentInput("");
    setLastGuessedWord(null);
    
    const advance = () => {
      setCurrentLevelNum(nextLevelNum);
      setTransitionState('fading-in');
      setTimeout(() => setTransitionState('idle'), 1500);
    };

    if (levels[`level_${nextLevelNum}`]) {
      advance();
    } else {
      const nextLevelRef = ref(db, `levels/level_${nextLevelNum}`);
      const snapshot = await get(nextLevelRef);
      if (snapshot.exists()) {
        const nextLevelData = snapshot.val();
        const updatedLevels = { ...levels, [`level_${nextLevelNum}`]: nextLevelData };
        setLevels(updatedLevels);
        localStorage.setItem('game_levels', JSON.stringify(updatedLevels));
        advance();
      }
    }
  }, [currentLevelNum, levels, totalLevelsInDb, userId, userIsAnon]);
  
  const handleGuess = useCallback(() => {
    if (currentInput.length === 0) return;
    const normalizedGuess = normalizeString(currentInput);
    setCurrentInput("");
    const normalizedCorrectAnswers = correctAnswers.map(answer => normalizeString(answer));
    const correctIndex = normalizedCorrectAnswers.indexOf(normalizedGuess);
    const originalAnswer = correctAnswers[correctIndex];

    if (correctIndex !== -1 && !guessedWords.includes(originalAnswer)) {
      const newGuessedWords = [...guessedWords, originalAnswer];
      setGuessedWords(newGuessedWords);
      setCoins(prev => prev + 5);
      setLastGuessedWord(originalAnswer);
      setShowCoinReward(true);
      setTimeout(() => setShowCoinReward(false), 1500);

      if (newGuessedWords.length === correctAnswers.length) {
        setShowConfetti(true);
        if(userId && analyticsReady) {
          logEvent(initializeAnalytics(), 'level_complete', {
            level_number: currentLevelNum,
          });
        }
        setTimeout(() => {
          setTransitionState('fading-out');
          setTimeout(() => advanceToNextLevel(), 500);
        }, 3000);
      }
    } else {
      setGuessStatus('failure');
      setTimeout(() => setGuessStatus('idle'), 500);
    }
  }, [currentInput, correctAnswers, guessedWords, advanceToNextLevel, currentLevelNum, userId, analyticsReady]);
  
  const handleHint = () => {
    if (coins < 15) return;
    const allUnguessedLetters = normalizeString(unguessedWords.join('')).replace(/[^A-Z]/g, '');
    const availableLetters = [...new Set(allUnguessedLetters.split(''))].filter(
      (letter) => !revealedLetters.has(letter)
    );
    if (availableLetters.length === 0) return;
    const letterToReveal = availableLetters[Math.floor(Math.random() * availableLetters.length)];
    setCoins(prev => prev - 15);
    setRevealedLetters(new Set(revealedLetters).add(letterToReveal));
    setJustRevealedLetter(letterToReveal);
    setTimeout(() => setJustRevealedLetter(null), 1000);
  };

  const handleGoogleLogin = async () => {
    try {
        setIsLoading(true);
        await signInWithPopup(auth, googleProvider);
    } catch (error) {
        console.error("Google sign-in failed:", error);
        alert(`Falha no login com Google: ${error.message}`);
    } finally {
        setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
        await signOut(auth);
    } catch (error) {
        console.error("Logout failed:", error);
    }
  };

  useEffect(() => {
    if (viewState !== 'game') return;
    const handleKeyDown = (event) => {
      if (transitionState !== 'idle' || isMenuOpen) return; 
      if (event.key === 'Enter') {
        handleGuess();
        return;
      }
      if (event.key === 'Backspace') {
        setCurrentInput(prev => prev.slice(0, -1));
        return;
      }
      if (validChars.special.has(event.key)) {
        setCurrentInput(prev => prev + event.key);
        return;
      }
      const key = event.key.toUpperCase();
      if (key.length === 1 && key >= 'A' && key <= 'Z' && validChars.letters.has(key)) {
        setCurrentInput(prev => prev + key);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [validChars, transitionState, handleGuess, isMenuOpen, viewState]);

  const navigateTo = (view, options = {}) => {
    const base = `/${userLang}`;
    let path = base;
    if (view === 'guide' && options.guideSlug) path = `${base}/guides/${options.guideSlug}`;
    else if (view !== 'home') path = `${base}${VIEW_TO_PATH[view] || '/'}`;

    if (window.location.pathname !== path) {
      window.history.pushState({ view, guideSlug: options.guideSlug || null }, '', path);
    }
    setGuideSlug(options.guideSlug || null);
    setViewState(view);
    setIsMenuOpen(false); 
  };

  const handleSelectLevel = (level) => {
    if (level <= currentLevelNum) {
        if (level === currentLevelNum) {
            navigateTo('game');
        } else {
            navigateTo('game');
        }
    }
  };

  if (isLoading) {
    return (
      <div className="game-viewport" style={{ background: HOME_BACKGROUND }}>
        <div className="loading-screen">
          <div className="spinner"></div>
          <div className="loading-text">LOADING...</div>
        </div>
      </div>
    );
  }

  let content;
  switch (viewState) {
    case 'home':
        content = (
            <HomePage 
                onPlay={() => navigateTo('game')}
                onLevels={() => navigateTo('levels')}
                onLogin={handleGoogleLogin}
                onLogout={handleLogout}
                isLoggedIn={userId !== null && !userIsAnon}
                userIsAnon={userIsAnon}
                userName={userName}
                userLang={userLang}
                onLanguageChange={handleLanguageChange}
                onNavigate={navigateTo}
            />
        );
        break;
    case 'levels':
        content = (
            <LevelList 
                totalLevels={totalLevelsInDb} 
                currentLevel={currentLevelNum}
                onSelectLevel={handleSelectLevel}
                onBack={() => navigateTo('home')}
                levelsData={levels} 
                userLang={userLang}
            />
        );
        break;
    case 'about':
        content = <About onBack={() => navigateTo('home')} />;
        break;
    case 'contact':
        content = <Contact onBack={() => navigateTo('home')} />;
        break;
    case 'privacy':
        content = <PrivacyPolicy onBack={() => navigateTo('home')} />;
        break;
    case 'how-to-play':
        content = <HowToPlay onBack={() => navigateTo('home')} />;
        break;
    case 'faq':
        content = <FAQ onBack={() => navigateTo('home')} />;
        break;
    case 'terms':
        content = <Terms onBack={() => navigateTo('home')} />;
        break;
	    case 'categories':
	        content = (
	          <Categories
	            onBack={() => navigateTo('home')}
	            onPlay={() => navigateTo('game')}
	            levelsData={levels}
	            userLang={userLang}
	          />
	        );
	        break;
	    case 'guides':
	        content = (
	          <Guides
	            basePath={`/${userLang}`}
	            onBack={() => navigateTo('home')}
	            onOpenArticle={(slug) => navigateTo('guide', { guideSlug: slug })}
	          />
	        );
	        break;
	    case 'guide':
	        content = (
	          <GuideArticle
	            slug={guideSlug}
	            basePath={`/${userLang}`}
	            onBack={() => navigateTo('guides')}
	            onPlay={() => navigateTo('game')}
	          />
	        );
	        break;
	    case 'updates':
	        content = <Updates onBack={() => navigateTo('home')} />;
	        break;
	    case 'game':
	        content = (
	            <>
                {isMenuOpen && <Menu 
                    onClose={() => setIsMenuOpen(false)} 
                    onNavigate={(page) => navigateTo(page)} 
                    isLoggedIn={userId !== null && !userIsAnon}
                    userIsAnon={userIsAnon}
                    onGoogleLogin={handleGoogleLogin}
                    onLogout={handleLogout}
                    userName={userName}
                    onHome={() => navigateTo('home')}
                />}
                
                {showConfetti && <Confetti recycle={false} numberOfPieces={400} gravity={0.15} />}
                <LevelTransition transitionState={transitionState} levelNumber={currentLevelNum} />
                <CoinReward show={showCoinReward} />

                <div className="game-container">
                    <div className="game-top">
                        <Header 
                            level={currentLevelNum} 
                            coins={coins}
                            userLang={userLang}
                            onLanguageChange={handleLanguageChange} 
                            onMenuClick={() => setIsMenuOpen(true)}
                        />
                    </div>

                    <div className="game-middle">
                        <div className="board-container">
                            <div className="theme-display">
                                <p>{currentTheme}</p>
                            </div>
                            <Board
                                answers={correctAnswers}
                                guessedWords={guessedWords}
                                lastGuessedWord={lastGuessedWord}
                                revealedLetters={revealedLetters}
                                justRevealedLetter={justRevealedLetter}
                            />
                        </div>
                    </div>

                    <div className="game-bottom">
                        <InputBar
                            currentInput={currentInput}
                            onGuess={handleGuess}
                            onHint={handleHint}
                            guessStatus={guessStatus}
                            canAffordHint={coins >= 15}
                        />
                        <Keyboard
                            validChars={validChars}
                            onKeyClick={(char) => setCurrentInput(prev => prev + char)}
                            onBackspace={() => setCurrentInput(prev => prev.slice(0, -1))}
                            language={userLang}
                        />
                    </div>
                </div>
            </>
        );
        break;
    default:
        content = <div>Error: Unknown State</div>;
  }

  return (
    <div className="game-viewport" style={{ background: displayedBackground }}>
      <CookieConsentBanner 
        onAccept={handleCookieAccept}
        onReject={handleCookieReject}
        onReadPolicy={() => navigateTo('privacy')}
      />
      {content}
    </div>
  );
}

export default Game;
